import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { settingsApi, usersApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  DataTable,
  FilterBar,
  PageHeader,
  SearchField,
  StatusBadge,
  SwitchField,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import type { UserSummary } from '../../api/endpoints.js';
import { RegisterPanel } from '../reports/register-panel.js';

/**
 * The users of this laboratory.
 *
 * Deactivated users are LISTED BY DEFAULT, clearly marked. They are not removed
 * from the record: their names remain attached to work they did, and hiding
 * them would make that history look authorless.
 *
 * Built entirely from the shared component library — the table, the filters,
 * the badges, the loading and empty states — so it inherits RTL behaviour,
 * keyboard handling and responsive rules rather than restating them.
 */
export function UsersPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();

  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [page, setPage] = useState(1);

  // The laboratory's time zone, so timestamps read in local terms even though
  // they are stored in UTC.
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    enabled: can(PERMISSIONS.SETTINGS_VIEW),
    retry: false,
  });

  const users = useQuery({
    queryKey: ['users', { page, search, includeInactive }],
    queryFn: () => usersApi.list({ page, search, includeInactive }),
  });

  const timezone = settings.data?.timezone;

  const columns: readonly DataColumn<UserSummary>[] = [
    {
      key: 'name',
      header: t('users.name'),
      render: (user) => (
        <Link
          to={`/users/${user.id}`}
          className="font-semibold text-primary-700 underline-offset-2 hover:underline"
        >
          <bdi>{user.displayName}</bdi>
        </Link>
      ),
    },
    {
      key: 'email',
      header: t('users.email'),
      render: (user) => (
        <span className="text-default-600" dir="ltr">
          {user.email}
        </span>
      ),
    },
    {
      key: 'role',
      header: t('users.role'),
      render: (user) => <StatusBadge label={t(`roles.${user.role}`)} />,
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (user) => (
        <StatusBadge
          label={user.isActive ? t('common.active') : t('common.inactive')}
          tone={user.isActive ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'lastSignIn',
      header: t('users.lastSignIn'),
      render: (user) => (
        <span className="text-default-600">
          {formatDateTime(user.lastLoginAt, {
            locale,
            ...(timezone ? { timezone } : {}),
          }) ?? t('common.never')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (user) => (
        <ActionButton
          priority="tertiary"
          size="sm"
          onPress={() => navigate(`/users/${user.id}`)}
        >
          {can(PERMISSIONS.USERS_UPDATE) ? t('common.edit') : t('common.view')}
        </ActionButton>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('users.title')}
        subtitle={t('users.subtitle')}
        breadcrumbs={[{ label: t('nav.users') }]}
        actions={
          can(PERMISSIONS.USERS_CREATE) ? (
            <ActionButton priority="primary" onPress={() => navigate('/users/new')}>
              {t('users.create')}
            </ActionButton>
          ) : null
        }
      />

      <RegisterPanel
        toolbar={
          <FilterBar>
            <SearchField
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              placeholder={t('users.searchPlaceholder')}
            />
            <SwitchField
              label={t('users.showDeactivated')}
              value={includeInactive}
              onChange={(value) => {
                setIncludeInactive(value);
                setPage(1);
              }}
            />
          </FilterBar>
        }
      >
        <DataTable
          label={t('users.title')}
          columns={columns}
          rows={users.data?.data ?? []}
          rowKey={(user) => user.id}
          isLoading={users.isLoading}
          error={users.error}
          onRetry={() => void users.refetch()}
          emptyContent={t('users.noneFound')}
          page={page}
          totalPages={users.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </RegisterPanel>
    </>
  );
}
